import { Injectable } from '@angular/core';
import { ProjectSettingsService } from '@core/services/common/project-settings.service';
import { EndpointConstants } from '@core/services/endpoints-configurations/endpoint-constants';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { PlantListInterface } from '../../../pages/record/interfaces';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ManageIdbService } from '../../../idb/manage-idb.service';
import { IdbCatalogName } from '../../../idb/idb-catalog-name.enum';
import { IAdminManagerCompanies } from '@core/models/admin-manager/companies.model';
import { IBusinessPlanByPlant } from '@core/models/admin-manager/businessplanbyplant.model';
import { ICompanyModel } from '@core/models/admin/company/company.model';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { ISelectPlant } from 'src/app/pages/orders/delivery-schedule/components/filters-delivery-schedule/models/filters.model';
import { HttpClientRequestService } from '@core/request-services/httpclient.request.services';
import { ITimeZone } from '@core/models/admin/googlemaps/ITimeZone';
import { PlantDataReceived } from 'src/app/pages/dispatch/services/signalr.service';
import { BatchConfig } from '@core/models/shared/batch-config.model';
import { Store } from '@ngrx/store';
import { loadPlantListCompany } from 'src/app/store/plant-list-company/plant-list-company.actions';
import { selectPlantListByKey } from 'src/app/store/plant-list-company/plant-list-company.selectors';
import { loadPlantListUserGuid } from 'src/app/store/plant-list-userguid/plant-list-userguid.actions';
import { selectPlantListUserGuid } from 'src/app/store/plant-list-userguid/plant-list-userguid.selectors';
import { PlantNetworkStatus } from 'src/app/store/plant-network-status/plant-network-status.models';
import { GlobalEndpointConstants } from '@core/services/endpoints-configurations/global-endpoint.constants';

@Injectable({
  providedIn: 'root',
})
export class PlantManagementService {
  public plantStatusUpdate: BehaviorSubject<PlantDataReceived> = new BehaviorSubject<PlantDataReceived>({
    plantCode: '',
    changeType: null,
    objectCode: '',
  });
  options = {
    headers: new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Ocp-Apim-Subscription-Key', environment.apimSubscriptionKey),
  };

  // Cached per companyGuid so repeated calls within the same session (e.g. re-opening order-taking)
  // reuse the in-flight/last response instead of re-POSTing the full plant list every time.
  private plantListByCompanyCache$ = new Map<string, Observable<PlantListInterface[]>>();

  constructor(
    private settings: ProjectSettingsService,
    private http: HttpClient,
    private idb: ManageIdbService,
    private cookieService: CookieService,
    private repo: HttpClientRequestService,
    private store: Store<Store>,
  ) {}

  getPlantListByCompany(): Observable<any> {
    const request = {} as IBusinessPlanByPlant;
    request.CompanyGuid = this.cookieService.get(environment.CompanyGuid).toUpperCase();

    return new Observable((subcriber) => {
      const url: string = EndpointConstants.ApiPlantsByCompany;
      this.repo.post(url, request).subscribe(
        (value) => {
          subcriber.next(value);
        },
        (error) => {
          subcriber.error(error);
        },
      );
    });
  }

  getPlantsByCompany(companyGuid: string): Observable<any> {
    const request = {} as IBusinessPlanByPlant;
    request.CompanyGuid = companyGuid.toUpperCase();

    return new Observable((subcriber) => {
      const url: string = EndpointConstants.ApiPlantsByCompany;
      this.repo.post(url, request).subscribe(
        (value) => {
          subcriber.next(value);
        },
        (error) => {
          subcriber.error(error);
        },
      );
    });
  }

  getLocalPlantCode(): Observable<string> {
    const userGuid = this.cookieService.get(environment.UserGuid).toUpperCase().toUpperCase();
    const url = `${EndpointConstants.ApiGetBatchConfig}`;
    const request = {} as IBusinessPlanByPlant;
    request.CompanyGuid = this.cookieService.get(environment.CompanyGuid).toUpperCase();
    request.UserGuid = userGuid;

    return this.http.get<BatchConfig>(url, this.options).pipe(
      map((config) => config.plantCode),
      catchError(() => of('')),
    );
  }

  getCompany(companyGuid: string): Observable<IAdminManagerCompanies> {
    const normalizedCompanyGuid = companyGuid.toUpperCase();
    const request = {} as IBusinessPlanByPlant;
    request.CompanyGuid = normalizedCompanyGuid;
    const url: string = EndpointConstants.ApiGetCompany;
    return this.repo
      .post(url, request)
      .pipe(
        switchMap((response: IAdminManagerCompanies) =>
          from(this.idb.saveCompanyInfo(response as unknown as ICompanyModel)).pipe(map(() => response)),
        ),
      );
  }

  public getPlantListAndSaveByCompany(): void {
    const userGuid = this.cookieService.get(environment.UserGuid).toUpperCase().toUpperCase();
    const url = this.settings.generateEndpoint(EndpointConstants.ApiPlantsByCompany + '?userGuid=' + userGuid);
    const request = {} as IBusinessPlanByPlant;
    request.CompanyGuid = this.cookieService.get(environment.CompanyGuid).toUpperCase();
    this.http
      .post<PlantListInterface[]>(url, request, this.options)
      .pipe(
        tap(async (result) => {
          await this.idb.savePlants(result);
        }),
        catchError((e) => {
          return of([]);
        }),
      )
      .subscribe();
  }

  public getPlantListAndSaveByCompanyReturn(): Observable<PlantListInterface[]> {
    const companyGuid = this.cookieService.get(environment.CompanyGuid).toUpperCase();
    const cached$ = this.plantListByCompanyCache$.get(companyGuid);
    if (cached$) {
      return cached$;
    }

    const url = this.settings.generateEndpoint(EndpointConstants.ApiPlantsByCompany);
    const request = {} as IBusinessPlanByPlant;
    request.CompanyGuid = companyGuid;

    const response$ = this.http.post<PlantListInterface[]>(url, request, this.options).pipe(
      catchError((e) => {
        // Drop the cache entry on failure so the next caller retries instead of replaying the error forever.
        this.plantListByCompanyCache$.delete(companyGuid);
        return of([]);
      }),
      shareReplay(1),
    );
    this.plantListByCompanyCache$.set(companyGuid, response$);
    return response$;
  }

  public getPlantListAndSave(): void {
    console.error('getPlantListAndSave');
    const userGuid = this.cookieService.get(environment.UserGuid).toUpperCase().toUpperCase();
    const url = this.settings.generateEndpoint(EndpointConstants.ApiPlants + '?userGuid=' + userGuid);
    this.http
      .get<PlantListInterface[]>(url, this.options)
      .pipe(
        tap(async (result) => {
          await this.idb.savePlants(result);
        }),
        catchError((e) => {
          return of([]);
        }),
      )
      .subscribe();
  }

  public getPlantList(): Observable<PlantListInterface[]> {
    this.store.dispatch(loadPlantListUserGuid());
    return this.store.select(selectPlantListUserGuid(this.cookieService.get(environment.UserGuid).toUpperCase())).pipe(
      filter((response) => Boolean(response)),
      map((plantList) => JSON.parse(JSON.stringify(plantList))),
      take(1),
    );
  }

  public getPlantListWithCompany(): Observable<PlantListInterface[]> {
    this.store.dispatch(loadPlantListCompany());
    const userGuid = this.cookieService.get(environment.UserGuid).toUpperCase().toUpperCase();
    const companyGuid = this.cookieService.get(environment.CompanyGuid).toUpperCase().toUpperCase();
    return this.store.select(selectPlantListByKey(userGuid, companyGuid)).pipe(
      filter((response) => Boolean(response)),
      map((plantList) => JSON.parse(JSON.stringify(plantList))),
      take(1),
    );
  }

  public updatePlantInformation(request: Partial<PlantListInterface>): Observable<any> {
    const url = this.settings.generateEndpoint(EndpointConstants.ApiPlants);
    return this.http.put(url, request, this.options).pipe(
      catchError((e) => {
        return of(e);
      }),
    );
  }

  public updatePlantStatus(newValue: PlantDataReceived): void {
    this.plantStatusUpdate.next(newValue);
  }

  public getInfoPlantByJobsite(jobsitePartnerId: number): Observable<ISelectPlant> {
    const options = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set('Ocp-Apim-Subscription-Key', environment.apimSubscriptionKey),
    };

    const url = this.settings.generateEndpoint(
      EndpointConstants.ApiPlantOrderTaking + '/GetInfoPlantByJobsite/' + jobsitePartnerId,
    );
    return this.http.get<ISelectPlant>(url, options).pipe(
      catchError((e) => {
        return of(e);
      }),
    );
  }

  /**
   * @deprecated Use PlantsBySubgroupApiService.getBySubgroup() instead — it uses NgRx cache.
   */
  public getInfoPlantsBySubgroup(subGroupId: number): Observable<ISelectPlant[]> {
    const options = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set('Ocp-Apim-Subscription-Key', environment.apimSubscriptionKey),
    };

    const url = this.settings.generateEndpoint(EndpointConstants.ApiPlantOrderTaking + '/' + subGroupId);
    return this.http.get<ISelectPlant>(url, options).pipe(
      catchError((e) => {
        return of(e);
      }),
    );
  }

  public calculateTimeDifference(latitude: number, longitude: number): Observable<ITimeZone> {
    const timestamp = new Date().getTime() / 1000; //1667318426//1668787226;
    const url = this.settings.generateEndpoint(
      EndpointConstants.ApiTimeZone,
      latitude.toString(),
      longitude.toString(),
      timestamp.toString(),
    );
    return this.http.get<ITimeZone>(url, this.options).pipe(
      catchError((e) => {
        return of(e);
      }),
    );
  }
  public async calculateTimeDifferenceAsync(latitude: number, longitude: number): Promise<ITimeZone> {
    const timestamp = new Date().getTime() / 1000; //1667318426//1668787226;
    const url = this.settings.generateEndpoint(
      EndpointConstants.ApiTimeZone,
      latitude.toString(),
      longitude.toString(),
      timestamp.toString(),
    );
    return await this.http
      .get<ITimeZone>(url, this.options)
      .pipe(
        catchError((e) => {
          return of(e);
        }),
      )
      .toPromise();
  }

  /**
   * Gets the network status of a plant
   * @param plantCode Plant code
   * @returns An observable of the latest plant network status
   */
  public GetPlantNetworkStatus(plantCode: string): Observable<PlantNetworkStatus> {
    const params = new HttpParams().set('plantCode', plantCode);
    const endpoint = `${GlobalEndpointConstants.CUM}${EndpointConstants.getPlantNetworkStatusEndpoint}`;
    const url = this.settings.generateEndpoint(endpoint, plantCode);
    return this.http.get<PlantNetworkStatus>(url, { params, ...this.options });
  }

  public GetPlantsNetworkStatus(PlantCodes: string): Observable<PlantNetworkStatus[]> {
    const endpoint = `${GlobalEndpointConstants.CUM}${EndpointConstants.getPlantsNetworkStatusEndpoint}${PlantCodes}`;
    const url = this.settings.generateEndpoint(endpoint);
    return this.http.get<PlantNetworkStatus[]>(url, this.options);
  }
}
